import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listBusinesses,
  getBusiness,
  createBusiness,
  updateBusiness,
  deleteBusiness,
} from '@/services/businessApi'
import type {
  ListBusinessesParams,
  CreateBusinessData,
  UpdateBusinessData,
} from '@/types'

// Hook for fetching business list with pagination
export function useBusinesses(params: ListBusinessesParams = {}) {
  return useQuery({
    queryKey: ['businesses', params],
    queryFn: () => listBusinesses(params),
    staleTime: 30000, // 30 seconds
  })
}

// Hook for fetching single business
export function useBusiness(id: string | null) {
  return useQuery({
    queryKey: ['business', id],
    queryFn: () => getBusiness(id!),
    enabled: !!id,
  })
}

// Hook for creating business
export function useCreateBusiness() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateBusinessData) => createBusiness(data),
    onSuccess: () => {
      // Invalidate business list to reflect new business
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

// Hook for updating business
export function useUpdateBusiness() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBusinessData }) =>
      updateBusiness(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific business query
      queryClient.invalidateQueries({ queryKey: ['business', id] })
      // Invalidate business list to reflect changes
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

// Hook for deleting business
export function useDeleteBusiness() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteBusiness(id),
    onSuccess: () => {
      // Invalidate business list to reflect deletion
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}
